<script setup>
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { emitter } from 'shared/helpers/mitt';
import PipelinesAPI from 'custom/dashboard/api/pipelines';
import SettingsLayout from 'dashboard/routes/dashboard/settings/SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import CrmSettingsHeader from '../crm/CrmSettingsHeader.vue';
import PipelineIconPicker from './components/PipelineIconPicker.vue';
import { PIPELINES_CHANGED } from '../crm/pipelineEvents';
import { DEFAULT_PIPELINE_ICON } from './pipelineIcons';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const pipelines = ref([]);
const isLoading = ref(true);
const togglingId = ref(null);

const fetchPipelines = async () => {
  try {
    const { data } = await PipelinesAPI.get();
    pipelines.value = data;
  } catch {
    useAlert(t('CARDS.PIPELINES_SETTINGS.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const editRoute = pipeline => ({
  name: 'pipelines_settings_edit',
  params: { ...route.params, pipelineId: pipeline.id },
});

// Create: a name and an icon; the stages are set up on the funnel's own page, opened next.
const createDialog = ref(null);
const newName = ref('');
const newIcon = ref(DEFAULT_PIPELINE_ICON);
const isCreating = ref(false);

const openCreate = () => {
  newName.value = '';
  newIcon.value = DEFAULT_PIPELINE_ICON;
  createDialog.value.open();
};

const create = async () => {
  isCreating.value = true;
  try {
    const { data } = await PipelinesAPI.create({
      pipeline: { name: newName.value.trim(), icon: newIcon.value },
    });
    emitter.emit(PIPELINES_CHANGED);
    useAlert(t('CARDS.PIPELINES_SETTINGS.FORM.CREATED'));
    createDialog.value.close();
    router.push(editRoute(data));
  } catch {
    useAlert(t('CARDS.PIPELINES_SETTINGS.FORM.CREATE_ERROR'));
  } finally {
    isCreating.value = false;
  }
};

// On/off. Turning a funnel off is confirmed first: it stops taking cards, forms included.
const setStatus = async (pipeline, status) => {
  togglingId.value = pipeline.id;
  try {
    const { data } = await PipelinesAPI.update(pipeline.id, {
      pipeline: { status },
    });
    Object.assign(pipeline, data);
    emitter.emit(PIPELINES_CHANGED);
    useAlert(
      status === 'active'
        ? t('CARDS.PIPELINES_SETTINGS.TURNED_ON')
        : t('CARDS.PIPELINES_SETTINGS.TURNED_OFF')
    );
  } catch {
    useAlert(t('CARDS.PIPELINES_SETTINGS.TOGGLE_ERROR'));
  } finally {
    togglingId.value = null;
  }
};

const turnOffDialog = ref(null);
const pendingTurnOff = ref(null);

const toggle = (pipeline, isOn) => {
  if (isOn) {
    setStatus(pipeline, 'active');
    return;
  }
  pendingTurnOff.value = pipeline;
  turnOffDialog.value.open();
};

const confirmTurnOff = async () => {
  await setStatus(pendingTurnOff.value, 'disabled');
  turnOffDialog.value.close();
};

// Delete: the backend only turns off a funnel that already has cards or forms.
const deleteDialog = ref(null);
const pendingDelete = ref(null);
const isDeleting = ref(false);

const askDelete = pipeline => {
  pendingDelete.value = pipeline;
  deleteDialog.value.open();
};

const confirmDelete = async () => {
  const { id } = pendingDelete.value;
  isDeleting.value = true;
  try {
    await PipelinesAPI.delete(id);
    await fetchPipelines();
    emitter.emit(PIPELINES_CHANGED);
    const kept = pipelines.value.some(pipeline => pipeline.id === id);
    useAlert(
      kept
        ? t('CARDS.PIPELINES_SETTINGS.DISABLED_INSTEAD')
        : t('CARDS.PIPELINES_SETTINGS.DELETED')
    );
    deleteDialog.value.close();
  } catch {
    useAlert(t('CARDS.PIPELINES_SETTINGS.DELETE_ERROR'));
  } finally {
    isDeleting.value = false;
  }
};

onMounted(fetchPipelines);
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="$t('CARDS.PIPELINES_SETTINGS.LOADING')"
    :no-records-found="!pipelines.length"
    :no-records-message="$t('CARDS.PIPELINES_SETTINGS.EMPTY')"
  >
    <template #header>
      <CrmSettingsHeader>
        <template #actions>
          <Button
            :label="$t('CARDS.PIPELINES_SETTINGS.NEW')"
            icon="i-lucide-plus"
            size="sm"
            data-testid="pipeline-new"
            @click="openCreate"
          />
        </template>
      </CrmSettingsHeader>
    </template>

    <template #body>
      <ul class="flex flex-col gap-3 mb-0 list-none ltr:pl-0 rtl:pr-0">
        <li
          v-for="pipeline in pipelines"
          :key="pipeline.id"
          class="flex items-center gap-4 px-4 py-3.5 transition-colors border rounded-xl border-n-weak bg-n-solid-1 hover:border-n-strong"
          :data-pipeline-id="pipeline.id"
        >
          <router-link
            :to="editRoute(pipeline)"
            class="flex items-center flex-1 min-w-0 gap-3"
            data-testid="pipeline-open"
          >
            <span
              class="flex items-center justify-center flex-shrink-0 rounded-lg size-10"
              :class="
                pipeline.status === 'active'
                  ? 'bg-n-brand/10 text-n-brand'
                  : 'bg-n-alpha-2 text-n-slate-10'
              "
            >
              <Icon :icon="pipeline.icon" class="size-5" />
            </span>
            <span class="flex flex-col flex-1 min-w-0 gap-1 overflow-hidden">
              <span class="flex items-center min-w-0 gap-2">
                <span
                  class="text-sm font-medium truncate"
                  :class="
                    pipeline.status === 'active'
                      ? 'text-n-slate-12'
                      : 'text-n-slate-10'
                  "
                >
                  {{ pipeline.name }}
                </span>
                <span
                  v-if="pipeline.system_managed"
                  class="flex-shrink-0 px-1.5 text-xs rounded bg-n-alpha-2 text-n-slate-11"
                  data-testid="pipeline-system"
                >
                  {{ $t('CARDS.PIPELINES_SETTINGS.SYSTEM') }}
                </span>
                <span
                  v-if="pipeline.status !== 'active'"
                  class="flex-shrink-0 px-1.5 text-xs rounded bg-n-amber-3 text-n-amber-11"
                  data-testid="pipeline-off"
                >
                  {{ $t('CARDS.PIPELINES_SETTINGS.OFF') }}
                </span>
              </span>
              <span
                class="text-xs truncate text-n-slate-10"
                data-testid="pipeline-stages"
              >
                {{
                  pipeline.stage_names?.length
                    ? pipeline.stage_names.join(' › ')
                    : $t('CARDS.PIPELINES_SETTINGS.NO_STAGES')
                }}
              </span>
            </span>
          </router-link>
          <div class="flex items-center flex-shrink-0 gap-1">
            <Switch
              :model-value="pipeline.status === 'active'"
              :disabled="togglingId === pipeline.id"
              :aria-label="
                $t('CARDS.PIPELINES_SETTINGS.TOGGLE', { name: pipeline.name })
              "
              class="ltr:mr-2 rtl:ml-2"
              data-testid="pipeline-toggle"
              @update:model-value="toggle(pipeline, $event)"
            />
            <Button
              v-tooltip.top="$t('CARDS.PIPELINES_SETTINGS.EDIT')"
              icon="i-lucide-pencil"
              size="sm"
              variant="ghost"
              color="slate"
              class="max-sm:hidden"
              data-testid="pipeline-edit"
              @click="router.push(editRoute(pipeline))"
            />
            <Button
              v-if="!pipeline.system_managed"
              v-tooltip.top="$t('CARDS.PIPELINES_SETTINGS.DELETE')"
              icon="i-lucide-trash-2"
              size="sm"
              variant="ghost"
              color="ruby"
              data-testid="pipeline-delete"
              @click="askDelete(pipeline)"
            />
            <span v-else class="size-8 max-sm:hidden" aria-hidden="true" />
          </div>
        </li>
      </ul>
    </template>
  </SettingsLayout>

  <Dialog
    ref="createDialog"
    :title="$t('CARDS.PIPELINES_SETTINGS.FORM.CREATE_TITLE')"
    :description="$t('CARDS.PIPELINES_SETTINGS.FORM.CREATE_DESCRIPTION')"
    :confirm-button-label="$t('CARDS.PIPELINES_SETTINGS.FORM.CREATE')"
    :disable-confirm-button="!newName.trim()"
    :is-loading="isCreating"
    data-testid="pipeline-create-dialog"
    @confirm="create"
  >
    <div class="flex items-end gap-2">
      <PipelineIconPicker v-model="newIcon" />
      <Input
        v-model="newName"
        :label="$t('CARDS.PIPELINES_SETTINGS.FORM.NAME')"
        :placeholder="$t('CARDS.PIPELINES_SETTINGS.FORM.NAME_PLACEHOLDER')"
        class="flex-1"
        data-testid="pipeline-new-name"
      />
    </div>
  </Dialog>

  <Dialog
    ref="turnOffDialog"
    type="alert"
    :title="
      $t('CARDS.PIPELINES_SETTINGS.TURN_OFF.TITLE', {
        name: pendingTurnOff?.name,
      })
    "
    :description="$t('CARDS.PIPELINES_SETTINGS.TURN_OFF.DESCRIPTION')"
    :confirm-button-label="$t('CARDS.PIPELINES_SETTINGS.TURN_OFF.CONFIRM')"
    :is-loading="!!togglingId"
    data-testid="pipeline-turn-off-dialog"
    @confirm="confirmTurnOff"
  />

  <Dialog
    ref="deleteDialog"
    type="alert"
    :title="
      $t('CARDS.PIPELINES_SETTINGS.DELETE_DIALOG.TITLE', {
        name: pendingDelete?.name,
      })
    "
    :description="$t('CARDS.PIPELINES_SETTINGS.DELETE_DIALOG.DESCRIPTION')"
    :confirm-button-label="$t('CARDS.PIPELINES_SETTINGS.DELETE_DIALOG.CONFIRM')"
    :is-loading="isDeleting"
    data-testid="pipeline-delete-dialog"
    @confirm="confirmDelete"
  />
</template>
